import { type Session } from '@nhost/nhost-js/auth'
import React, {
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { identify, track } from 'src/analytics'
import { resetApolloClient } from 'src/apolloClient'
import { nhost } from 'src/nhost'
import settings from 'src/settings'

// Check token every 4 minutes (well within the 15-min JWT expiry)
const REFRESH_CHECK_INTERVAL_MS = 4 * 60_000

export interface AuthContextType {
  user: Session['user'] | null
  session: Session | null
  isAuthenticated: boolean
  isLoading: boolean
}

export const AuthContext = createContext<AuthContextType | null>(null)

// Direct refresh via fetch — bypasses nhost.refreshSession() which uses
// navigator.locks and can hang after sleep/wake cycles
async function refreshTokenDirect(): Promise<boolean> {
  const currentSession = nhost.getUserSession()
  if (!currentSession?.refreshToken) return false

  const authUrl = settings.nhost.authUrl || ''
  try {
    const res = await fetch(`${authUrl}/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: currentSession.refreshToken }),
    })
    if (!res.ok) {
      console.error('[Auth] Token refresh failed:', res.status)
      return false
    }
    const data = await res.json()
    const session = data.session || data
    if (session?.accessToken && session?.refreshToken) {
      nhost.sessionStorage.set(session)
      return true
    }
    return false
  } catch (err) {
    console.error('[Auth] Token refresh error:', err)
    return false
  }
}

// Check if the stored JWT is expired or about to expire
function isTokenExpiringSoon(marginMs = 2 * 60_000): boolean {
  const session = nhost.getUserSession()
  if (!session) return false
  const decoded = (session as { decodedToken?: { exp?: number } }).decodedToken
  if (!decoded?.exp) return true // no exp → needs refresh
  return decoded.exp - Date.now() < marginMs
}

async function exchangeRefreshToken(refreshToken: string): Promise<boolean> {
  const authUrl = settings.nhost.authUrl || ''
  const url = `${authUrl}/token`
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    })
    if (!res.ok) {
      const text = await res.text()
      console.error('[Auth] Token exchange HTTP error:', res.status, text)
      return false
    }
    const data = await res.json()
    const session = data.session || data
    if (session?.accessToken && session?.refreshToken) {
      nhost.sessionStorage.set(session)
      return true
    }
    console.error('[Auth] No session in response:', Object.keys(data))
    return false
  } catch (err) {
    console.error('[Auth] Token exchange failed:', err)
    return false
  }
}

function signOutAndRedirect() {
  nhost.clearSession()
  resetApolloClient().catch(() => {})
  window.location.href = '/'
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<Session['user'] | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false)

  useEffect(() => {
    const currentSession = nhost.getUserSession()
    setUser(currentSession?.user || null)
    setSession(currentSession)
    setIsAuthenticated(!!currentSession)

    // Check if we're waiting for a token exchange (SSO/password-reset callback)
    const refreshToken = new URLSearchParams(window.location.search).get(
      'refreshToken'
    )
    if (refreshToken) {
      exchangeRefreshToken(refreshToken)
        .then((success) => {
          const url = new URL(window.location.href)
          url.searchParams.delete('refreshToken')
          window.history.replaceState({}, '', url.toString())
          if (!success) {
            console.error('[Auth] Token exchange failed, signing out')
            nhost.clearSession()
            resetApolloClient().catch(() => {})
          }
        })
        .finally(() => {
          setIsLoading(false)
        })
    } else {
      setIsLoading(false)
    }

    const unsubscribe = nhost.sessionStorage.onChange((currentSession) => {
      setUser(currentSession?.user || null)
      setSession(currentSession)
      setIsAuthenticated(!!currentSession)
    })

    return () => {
      unsubscribe()
    }
  }, [])

  // Who is in the session, in pseudonymous form: user id and the onboarding
  // answers already collected in the user metadata, never the email or name.
  useEffect(() => {
    if (!user) return
    identify({
      userId: user.id,
      locale: user.locale,
      emailVerified: user.emailVerified,
      onboardingRole: user.metadata?.onboardingRole as string | undefined,
      onboardingObjective: user.metadata?.onboardingObjective as
        | string
        | undefined,
      onboardingSource: user.metadata?.onboardingSource as string | undefined,
    })
  }, [user?.id, user?.emailVerified, user?.metadata])

  // Verification happens through an email link, so the flag flips on a session
  // refresh rather than on a user action in this tab.
  const wasEmailVerified = useRef<boolean>()
  useEffect(() => {
    if (!user) return
    const previous = wasEmailVerified.current
    wasEmailVerified.current = user.emailVerified
    if (previous === false && user.emailVerified) track('auth_email_verified')
  }, [user?.id, user?.emailVerified])

  // Refresh token if expired/expiring, auto-logout on failure
  const refreshIfNeeded = useCallback(async () => {
    if (!nhost.getUserSession()?.accessToken) return
    if (!isTokenExpiringSoon()) return

    const success = await refreshTokenDirect()
    if (!success) {
      console.warn('[Auth] Cannot refresh token — signing out')
      signOutAndRedirect()
    }
  }, [])

  // 1. Periodic refresh timer (handles normal expiry while tab is active)
  const refreshTimerRef = useRef<ReturnType<typeof setInterval>>()

  useEffect(() => {
    if (isAuthenticated) {
      refreshTimerRef.current = setInterval(
        refreshIfNeeded,
        REFRESH_CHECK_INTERVAL_MS
      )
    }
    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current)
      }
    }
  }, [isAuthenticated, refreshIfNeeded])

  // 2. Refresh on wake from sleep (visibilitychange fires when tab becomes visible)
  useEffect(() => {
    if (!isAuthenticated) return

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshIfNeeded()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [isAuthenticated, refreshIfNeeded])

  const value: AuthContextType = {
    user,
    session,
    isAuthenticated,
    isLoading,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
